import { useContext, useEffect, useRef, useState } from "react";
import ApplicationContext from "../context/application/ApplicationContext";
import TenantContext from "../context/tenant/TenantContext";
import AuthContext from "../context/auth/AuthContext";
import useTranslator from "../hooks/useTranslator";
import SkorDefaultLoader from "../assets/img/skorDefaultLoader.gif";
import { haveValue, isValidEmail, toast } from "../helper/common";
import {
  getInitials,
  resolveParticipantName,
} from "../helper/applicationHelpers";
import UserIcon from "../assets/img/user-icon.svg";

/* ── small avatar circle ── */
const Avatar = ({ name, size = 40, bg = "#a3eb64", color = "#1E2528" }) => (
  <div
    style={{
      width: size,
      height: size,
      minWidth: size,
      borderRadius: "50%",
      background: bg,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: 700,
      fontSize: size * 0.35,
      color,
      textTransform: "uppercase",
    }}
  >
    {getInitials(name)}
  </div>
);

const resolveCreatorName = (created_by) => {
  if (!created_by || typeof created_by === "string") return "";
  const fname = created_by.fname || created_by.firstname || "";
  const lname = created_by.lname || created_by.lastname || "";
  return `${fname} ${lname}`.trim() || created_by.email || "";
};

const resolveCreatorEmail = (created_by) => {
  if (!created_by || typeof created_by === "string") return null;
  return created_by.email ?? null;
};

const isSharedWithLandlord = (app, landlordId) => {
  if (!landlordId) return false;
  const profiles = app.shareRequests ?? [];
  const extracted_landlord_id = landlordId?.split?.("_")?.[0] ?? landlordId;
  const check = (entry) => {
    const id = entry?.landlord_id?._id;
    return id === extracted_landlord_id;
  };
  return profiles.some(check);
};

/* ── SCREEN 1 – choice ── */
const ChoiceScreen = ({
  landlordData,
  pendingAppsForLandlord,
  onSelectIndividual,
  onSelectGroup,
  onSelectCreate,
  onClose,
  _t,
  user,
}) => {
  const landlordName =
    `${landlordData?.fname_companyName ?? ""} ${landlordData?.lname ?? ""}`.trim() ||
    landlordData?.email ||
    _t("landlord", "Landlord");

  return (
    <div className="isf-modal">
      {/* Dark header */}
      <div className="isf-header">
        <button className="isf-close-btn" onClick={onClose} aria-label="close">
          ✕
        </button>
        <div className="isf-header-landlord">
          <Avatar name={landlordName} size={44} bg="#a3eb64" color="#1E2528" />
          <div className="isf-header-landlord-info">
            <span className="isf-header-landlord-name">{landlordName}</span>
            <span className="isf-header-landlord-sub">
              {_t(
                "hasInvitedYouToShareYourProfile",
                "has invited you to share your profile"
              )}
            </span>
          </div>
        </div>
        <h2 className="isf-header-title">
          {_t(
            "whatWouldYouLikeToShare",
            "What would you like to share with this landlord?"
          )}
        </h2>
      </div>

      {/* Options */}
      <div className="isf-options">
        {/* Individual profile */}
        <button className="isf-option-card" onClick={onSelectIndividual}>
          <div className="isf-option-icon isf-option-icon-person">
            <img src={UserIcon} alt="User" />
          </div>
          <div className="isf-option-text">
            <span className="isf-option-title">
              {_t("myIndividualProfile", "My individual profile")}
            </span>
            <span className="isf-option-sub">
              {_t("shareOnlyYourOwnProfile", "Share only your own profile.")}
            </span>
          </div>
          <svg
            className="isf-chevron"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>

        {/* Existing group cards */}
        {pendingAppsForLandlord.map((app) => {
          const creatorName = resolveCreatorName(app.created_by);
          const participants = app.participants ?? [];
          const meEntry = participants.find(
            (p) => p.email?.toLowerCase() === user?.email?.toLowerCase()
          );
          return (
            <div
              key={app._id}
              className="isf-option-card isf-option-card-group"
            >
              <span className="isf-flatmate-badge">
                {_t(
                  "yourFlatmateAlreadyAddedYou",
                  "Your flatmate already added you"
                )}
              </span>
              <div
                className="isf-option-card-inner"
                onClick={() => onSelectGroup(app)}
              >
                <div className="isf-option-icon isf-option-icon-group">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </div>
                <div className="isf-option-text">
                  <span className="isf-option-title">{app.title}</span>
                  <div className="isf-chips-row">
                    {participants.slice(0, 4).map((p, i) => {
                      const name = resolveParticipantName(p);
                      const isMe =
                        p.email?.toLowerCase() === user?.email?.toLowerCase();
                      return (
                        <div
                          key={i}
                          className={`isf-chip${isMe ? " isf-chip-me" : ""}`}
                          title={name}
                        >
                          {isMe ? "You" : getInitials(name)}
                        </div>
                      );
                    })}
                  </div>
                  <span className="isf-option-sub">
                    {_t(
                      "flatmateAlreadySharedWithLandlord",
                      `${creatorName} has already shared this group with ${`${landlordData?.fname_companyName ?? ""} ${landlordData?.lname ?? ""}`.trim() || "the landlord"}. By accepting, your profile will be included too.`
                    )}
                  </span>
                </div>
                <svg
                  className="isf-chevron"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </div>
            </div>
          );
        })}

        {/* Create new group */}
        <button
          className="isf-option-card isf-option-card-create"
          onClick={onSelectCreate}
        >
          <div className="isf-option-icon isf-option-icon-create">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
          </div>
          <div className="isf-option-text">
            <span className="isf-option-title">
              {_t("createANewGroup", "Create a new group")}
            </span>
            <span className="isf-option-sub">
              {_t(
                "inviteYourFlatmatesByEmail",
                "Invite your flatmates by email."
              )}
            </span>
          </div>
          <svg
            className="isf-chevron"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>
    </div>
  );
};

/* ── SCREEN 2 – join group ── */
const JoinGroupScreen = ({
  app,
  landlordData,
  onBack,
  onClose,
  onAccept,
  onDecline,
  processing,
  user,
  _t,
}) => {
  const creatorName = resolveCreatorName(app.created_by);
  const creatorEmail = resolveCreatorEmail(app.created_by);
  const participants = app.participants ?? [];
  const landlordName =
    `${landlordData?.fname_companyName ?? ""} ${landlordData?.lname ?? ""}`.trim() ||
    _t("theLandlord", "the landlord");

  const getRole = (p) => {
    const isOwner =
      creatorEmail && p.email?.toLowerCase() === creatorEmail.toLowerCase();
    if (isOwner) return "owner";
    const isMe = p.email?.toLowerCase() === user?.email?.toLowerCase();
    if (isMe) return "pending";
    return p.consent_status ?? "pending";
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case "owner":
        return _t("groupOwner", "Group owner");
      case "pending":
        return _t("pendingAcceptance", "Pending acceptance");
      case "accepted":
        return _t("accepted", "Accepted");
      case "rejected":
        return _t("rejected", "Rejected");
      default:
        return role;
    }
  };

  return (
    <div className="isf-modal isf-modal-sub">
      {/* Sub-screen header */}
      <div className="isf-sub-header">
        <button className="isf-back-btn" onClick={onBack}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
          {_t("back", "Back")}
        </button>
        <div className="isf-sub-title-block">
          <h2 className="isf-sub-title">
            {_t("joinGroup", `Join ${creatorName}'s group`)}
          </h2>
          <span className="isf-sub-landlord">
            {_t("with", "with")} {landlordName}
          </span>
        </div>
      </div>

      {/* Members section */}
      <div className="isf-body">
        <div className="isf-members-card">
          <span className="isf-members-label">
            {_t("groupMembers", "Group members")}
          </span>
          <div className="isf-members-list">
            {participants.map((p, i) => {
              const name = resolveParticipantName(p);
              const isMe =
                p.email?.toLowerCase() === user?.email?.toLowerCase();
              const role = getRole(p);
              return (
                <div key={i} className="isf-member-row">
                  <Avatar
                    name={isMe ? "You" : name}
                    size={40}
                    bg={role === "owner" ? "#a3eb64" : "#d8d8d8"}
                    color={role === "owner" ? "#1E2528" : "#555"}
                  />
                  <div className="isf-member-info">
                    <span className="isf-member-name">
                      {isMe ? _t("you", "You") : name}
                    </span>
                    <span className="isf-member-role">
                      {getRoleLabel(role)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <p className="isf-join-disclaimer">
          {_t(
            "byAcceptingYourProfileWillBeShared",
            `By accepting, your profile will be shared with ${landlordName} as part of this group. You can leave the group at any time.`
          )}
        </p>
      </div>

      {/* Footer */}
      <div className="isf-footer">
        <button
          className="isf-btn isf-btn-outline"
          onClick={onDecline}
          disabled={processing}
        >
          {_t("decline", "Decline")}
        </button>
        <button
          className="isf-btn isf-btn-primary"
          onClick={onAccept}
          disabled={processing}
        >
          {processing ? "..." : _t("acceptAndJoin", "Accept & join")}
        </button>
      </div>
    </div>
  );
};

/* ── SCREEN 3 – create group ── */
const CreateGroupScreen = ({ onBack, onClose, onCreateAndShare, user, _t }) => {
  const [nameInput, setNameInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [participants, setParticipants] = useState([]);
  const [emailError, setEmailError] = useState("");
  const [creating, setCreating] = useState(false);

  const handleAdd = () => {
    const trimName = nameInput.trim();
    const trimEmail = emailInput.trim().toLowerCase();
    if (!trimName) {
      setEmailError("Full name is required");
      return;
    }
    if (!trimEmail) return;
    if (!isValidEmail(trimEmail)) {
      setEmailError("Invalid email address");
      return;
    }
    if (trimEmail === user?.email?.toLowerCase()) {
      setEmailError("You cannot add yourself");
      return;
    }
    if (participants.some((p) => p.email === trimEmail)) {
      setEmailError("Email already added");
      return;
    }
    if (participants.length >= 4) {
      setEmailError("Maximum of 4 participants allowed");
      return;
    }
    setParticipants((prev) => [...prev, { name: trimName, email: trimEmail }]);
    setNameInput("");
    setEmailInput("");
    setEmailError("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleSubmit = async () => {
    if (participants.length === 0) {
      toast.error("Add at least one flatmate");
      return;
    }
    try {
      setCreating(true);
      await onCreateAndShare(participants);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="isf-modal isf-modal-light">
      {/* White header */}
      <div className="isf-light-header">
        <button className="isf-light-back-btn" onClick={onBack}>
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <h2 className="isf-light-title">Create a new group</h2>
        <button
          className="isf-light-close-btn"
          onClick={onClose}
          aria-label="close"
        >
          ✕
        </button>
      </div>

      {/* Body */}
      <div className="isf-body">
        {/* Single unified card: form + participant list */}
        <div className="isf-create-card">
          <div className="isf-create-form-section">
            <h4 className="isf-create-section-title">
              {_t("inviteYourFlatmatesByEmail")}
            </h4>
            <p className="isf-create-section-sub">
              {_t("inviteYourFlatmatesByEmailModelDescription")}
            </p>
            <div className="isf-create-inputs">
              <input
                className={`isf-create-input${emailError ? " is-invalid" : ""}`}
                placeholder="Full name"
                value={nameInput}
                onChange={(e) => {
                  setNameInput(e.target.value);
                  setEmailError("");
                }}
                onKeyDown={handleKeyDown}
              />
              <input
                type="email"
                className={`isf-create-input${emailError ? " is-invalid" : ""}`}
                placeholder="name@email.com"
                value={emailInput}
                onChange={(e) => {
                  setEmailInput(e.target.value.toLowerCase());
                  setEmailError("");
                }}
                onKeyDown={handleKeyDown}
              />
              <button
                className="isf-add-btn"
                onClick={handleAdd}
                disabled={!nameInput.trim() || !emailInput.trim()}
              >
                Add
              </button>
            </div>
            {emailError && <p className="isf-input-error">{emailError}</p>}
          </div>

          {participants.length > 0 && (
            <div className="isf-participant-list">
              {participants.map(({ name, email }) => (
                <div key={email} className="isf-participant-item">
                  <div className="isf-participant-info">
                    <span className="isf-participant-name">{name}</span>
                    <span className="isf-participant-email">{email}</span>
                  </div>
                  <span className="isf-participant-status">
                    Invitation will be sent
                  </span>
                  <button
                    className="isf-participant-remove"
                    onClick={() =>
                      setParticipants((prev) =>
                        prev.filter((p) => p.email !== email)
                      )
                    }
                    aria-label="remove"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="isf-footer">
        <button
          className="isf-btn isf-btn-outline"
          onClick={onBack}
          disabled={creating}
        >
          Back
        </button>
        <button
          className="isf-btn isf-btn-primary"
          onClick={handleSubmit}
          disabled={creating || participants.length === 0}
        >
          {creating ? "..." : "Create group & share"}
        </button>
      </div>
    </div>
  );
};

/* ── MAIN COMPONENT ── */
const InvitationShareModal = ({
  isOpen,
  landlordId, // cookies.requestProfileLink — may become undefined after cookies clear
  propertyId, // cookies.property_id
  onClose,
  onClearCookies,
  onNeedsConsent,
  onDone,
}) => {
  const { _t } = useTranslator();
  const { user } = useContext(AuthContext);
  const { getLandlordsConsentInfo, loadSharedProfiles } =
    useContext(TenantContext);
  const {
    pendingApplications,
    myApplications,
    createApplication,
    consentApplication,
    loadApplicationsData,
    openLandlordLink,
    loadLandlordRequests,
    acceptLandlordRequest,
  } = useContext(ApplicationContext);

  const [step, setStep] = useState("choice");
  const [selectedApp, setSelectedApp] = useState(null);
  const [landlordData, setLandlordData] = useState(null);
  const [landLordLoading, setLandlordLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  // Snapshot IDs so parent cookie-clearing doesn't affect in-flight actions
  const capturedLandlordId = useRef(null);
  const requestId = useRef(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (isOpen && landlordId && !fetchedRef.current) {
      fetchedRef.current = true;
      // Snapshot before cookies are cleared
      capturedLandlordId.current = landlordId;
      const capturedPropertyId = propertyId;

      setLandlordLoading(true);

      Promise.all([
        getLandlordsConsentInfo(landlordId),
        openLandlordLink(landlordId, capturedPropertyId),
        loadApplicationsData(),
      ])
        .then(([consentRes, linkRes]) => {
          setLandlordData(consentRes?.data ?? consentRes);
          // Store the request ID returned by the API
          requestId.current =
            linkRes?.data?._id ??
            linkRes?.success_res?._id ??
            linkRes?._id ??
            null;
          // Clear cookies now that the link has been registered
          onClearCookies();
          // Refresh landlord requests to surface any notifications
          return loadLandlordRequests();
        })
        .catch(() => {
          // Still clear cookies on failure to avoid re-triggering
          onClearCookies();
        })
        .finally(() => setLandlordLoading(false));
    }
    if (!isOpen) {
      fetchedRef.current = false;
      setStep("choice");
      setSelectedApp(null);
      requestId.current = null;
      capturedLandlordId.current = null;
    }
  }, [isOpen, landlordId]);

  const pendingAppsForLandlord = (pendingApplications ?? []).filter((app) =>
    isSharedWithLandlord(app, capturedLandlordId.current ?? landlordId)
  );

  const handleClose = () => {
    setStep("choice");
    setSelectedApp(null);
    onClose();
  };

  /* returns true if landlord requires consent the tenant hasn't given yet */
  const consentRequired = () => {
    if (!landlordData) return false;
    const insuranceEnabled = landlordData?.insurance_service_status;
    const affidavitEnabled = landlordData?.affidavit_activated;
    const hasInsuranceConsent =
      haveValue(user?.consents?.gdpr_consent) &&
      haveValue(user?.consents?.skor_consent);
    const hasAffidavitConsent = haveValue(user?.consents?.declaration_consent);
    return (
      (insuranceEnabled && !hasInsuranceConsent) ||
      (affidavitEnabled && !hasAffidavitConsent)
    );
  };

  /*
   * Gate every share action behind consent.
   * If consent is needed: close this modal, let parent show ConsentStepModal
   * with the captured action as the post-consent callback.
   * If consent is not needed: run action directly.
   */
  const withConsentGate = async (action) => {
    if (consentRequired()) {
      onNeedsConsent(landlordData, action);
      handleClose();
      return;
    }
    await action();
  };

  /* Individual profile → respond to the landlord request with no application */
  const handleIndividual = async () => {
    if (!requestId.current) return;
    const reqId = requestId.current;
    const userId = user?._id ?? localStorage.getItem("saveduserid");
    const shareAction = async () => {
      try {
        setProcessing(true);
        await acceptLandlordRequest(reqId);
        if (userId) await loadSharedProfiles(userId, "full");
        onDone();
      } catch {
        toast.error("Failed to share profile");
      } finally {
        setProcessing(false);
      }
    };
    await withConsentGate(shareAction);
  };

  /* Join existing group → consent to the application + respond to landlord request */
  const handleAcceptGroup = async () => {
    if (!selectedApp || !requestId.current) return;
    const appId = selectedApp._id;
    const reqId = requestId.current;
    const acceptAction = async () => {
      try {
        setProcessing(true);
        await Promise.all([
          consentApplication(appId, "accepted"),
          // acceptLandlordRequest(reqId, appId),
        ]);
        onDone();
      } catch {
        toast.error("Failed to join group");
      } finally {
        setProcessing(false);
      }
    };
    await withConsentGate(acceptAction);
  };

  /* Decline → reject the application invitation */
  const handleDeclineGroup = async () => {
    if (!selectedApp) {
      setStep("choice");
      setSelectedApp(null);
      return;
    }
    const appId = selectedApp._id;
    try {
      setProcessing(true);
      await consentApplication(appId, "rejected");
    } catch {
      toast.error("Failed to decline");
    } finally {
      setProcessing(false);
    }
    setStep("choice");
    setSelectedApp(null);
  };

  /*
   * Create group → create application first, then respond to landlord request
   * with the new application ID.
   * Consent is checked before any action — participants list is captured in closure.
   */
  const handleCreateAndShare = async (participants) => {
    if (!requestId.current) return;
    const reqId = requestId.current;
    const createAction = async () => {
      try {
        const defaultTitle = `${user?.fname}'s group`;
        const res = await createApplication({
          participants,
          title: defaultTitle,
        });
        const appId = res?.success_res?._id ?? res?._id;
        await acceptLandlordRequest(reqId, appId);
        onDone();
      } catch (err) {
        toast.error(
          err?.response?.data?.message ??
            _t("failedToCreateGroup", "Failed to create group")
        );
        throw err;
      }
    };
    await withConsentGate(createAction);
  };

  if (!isOpen) return null;

  return (
    <div className="wrapper-modal isf-overlay" onClick={handleClose}>
      <div className="isf-container" onClick={(e) => e.stopPropagation()}>
        {landLordLoading ? (
          <div className="isf-loading">
            <img
              src={SkorDefaultLoader}
              className="defaultSkorLoader"
              alt="Example"
            />
          </div>
        ) : step === "choice" ? (
          <ChoiceScreen
            landlordData={landlordData}
            pendingAppsForLandlord={pendingAppsForLandlord}
            onSelectIndividual={handleIndividual}
            onSelectGroup={(app) => {
              setSelectedApp(app);
              setStep("join-group");
            }}
            onSelectCreate={() => setStep("create-group")}
            onClose={handleClose}
            _t={_t}
            user={user}
            processing={processing}
          />
        ) : step === "join-group" ? (
          <JoinGroupScreen
            app={selectedApp}
            landlordData={landlordData}
            onBack={() => setStep("choice")}
            onClose={handleClose}
            onAccept={handleAcceptGroup}
            onDecline={handleDeclineGroup}
            processing={processing}
            user={user}
            _t={_t}
          />
        ) : (
          <CreateGroupScreen
            onBack={() => setStep("choice")}
            onClose={handleClose}
            onCreateAndShare={handleCreateAndShare}
            user={user}
            _t={_t}
          />
        )}
      </div>
    </div>
  );
};

export default InvitationShareModal;
